import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  apiFetch,
} from "../api/client";

import {
  tenantPath,
} from "../utils/tenantContext";

import "./RemoteCaptureControl.css";


async function requestJson(
  path,
  options = {}
) {
  const response =
    await apiFetch(
      tenantPath(path),
      options
    );

  let body = {};

  try {
    body =
      await response.json();
  } catch (_) {
    // keep empty body
  }

  if (!response.ok) {
    throw new Error(
      body.detail ||
      `Request failed (${response.status})`
    );
  }

  return body;
}


function RemoteCaptureControl() {
  const [status, setStatus] =
    useState(null);

  const [audit, setAudit] =
    useState([]);

  const [duration, setDuration] =
    useState(30);

  const [snaplen, setSnaplen] =
    useState(256);

  const [filter, setFilter] =
    useState("ip or ip6");

  const [working, setWorking] =
    useState(false);

  const [error, setError] =
    useState("");


  const load = useCallback(
    async () => {
      try {
        const [
          statusData,
          auditData,
        ] = await Promise.all([
          requestJson(
            "/network-protection/pcap/control/status"
          ),

          requestJson(
            "/network-protection/pcap/control/audit"
          ),
        ]);

        setStatus(
          statusData?.status || {}
        );

        setAudit(
          Array.isArray(
            auditData?.audit
          )
            ? auditData.audit
            : []
        );
      } catch (loadError) {
        setError(
          loadError.message
        );
      }
    },
    []
  );


  useEffect(() => {
    load();

    const timer =
      window.setInterval(
        load,
        3000
      );

    return () =>
      window.clearInterval(
        timer
      );
  }, [load]);


  const active =
    status?.ACTIVE === "YES";


  const startCapture =
    async () => {
      const confirmed =
        window.confirm(
          `Start an authorized ${duration}-second packet capture on enp0s5?`
        );

      if (!confirmed) return;

      try {
        setWorking(true);
        setError("");

        await requestJson(
          "/network-protection/pcap/control/start",
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              interface: "enp0s5",
              duration_seconds:
                Number(duration),
              snaplen:
                Number(snaplen),
              filter_expression:
                filter,
            }),
          }
        );

        await load();
      } catch (startError) {
        setError(
          startError.message
        );
      } finally {
        setWorking(false);
      }
    };


  const stopCapture =
    async () => {
      const confirmed =
        window.confirm(
          "Stop the active authorized capture?"
        );

      if (!confirmed) return;

      try {
        setWorking(true);
        setError("");

        await requestJson(
          "/network-protection/pcap/control/stop",
          {
            method: "POST",
          }
        );

        await load();
      } catch (stopError) {
        setError(
          stopError.message
        );
      } finally {
        setWorking(false);
      }
    };


  return (
    <section className="remoteCaptureControl">
      <div className="remoteCaptureTitle">
        <div>
          <span>
            CONTROLLED COLLECTION
          </span>

          <h3>
            Remote Packet Capture
          </h3>

          <p>
            Explicit analyst-controlled,
            bounded packet collection.
            Capture remains OFF by default.
          </p>
        </div>

        <div
          className={
            active
              ? "captureState active"
              : "captureState inactive"
          }
        >
          {active
            ? "CAPTURING"
            : "CAPTURE OFF"}
        </div>
      </div>

      <div className="remoteCaptureControls">
        <label>
          Interface

          <select
            disabled
            value="enp0s5"
          >
            <option value="enp0s5">
              enp0s5
            </option>
          </select>
        </label>

        <label>
          Duration

          <select
            value={duration}
            disabled={
              active || working
            }
            onChange={(event) =>
              setDuration(
                Number(
                  event.target.value
                )
              )
            }
          >
            <option value={10}>
              10 seconds
            </option>

            <option value={30}>
              30 seconds
            </option>

            <option value={60}>
              60 seconds
            </option>
          </select>
        </label>

        <label>
          Snap length

          <select
            value={snaplen}
            disabled={
              active || working
            }
            onChange={(event) =>
              setSnaplen(
                Number(
                  event.target.value
                )
              )
            }
          >
            <option value={96}>
              96 bytes
            </option>

            <option value={128}>
              128 bytes
            </option>

            <option value={256}>
              256 bytes
            </option>

            <option value={512}>
              512 bytes
            </option>
          </select>
        </label>

        <label>
          Filter

          <select
            value={filter}
            disabled={
              active || working
            }
            onChange={(event) =>
              setFilter(
                event.target.value
              )
            }
          >
            <option value="ip or ip6">
              All IP
            </option>

            <option value="tcp">
              TCP
            </option>

            <option value="udp">
              UDP
            </option>

            <option value="port 53">
              DNS / port 53
            </option>

            <option value="port 80 or port 443">
              Web / 80 + 443
            </option>
          </select>
        </label>
      </div>

      <div className="remoteCaptureActions">
        <button
          type="button"
          disabled={
            active || working
          }
          onClick={startCapture}
        >
          Start Capture
        </button>

        <button
          type="button"
          className="captureStopButton"
          disabled={
            !active || working
          }
          onClick={stopCapture}
        >
          Stop Capture
        </button>

        <button
          type="button"
          disabled={working}
          onClick={load}
        >
          Refresh Status
        </button>
      </div>

      {error ? (
        <div className="remoteCaptureError">
          {error}
        </div>
      ) : null}

      <div className="remoteCaptureStatus">
        <div>
          <span>Request</span>
          <strong>
            {status?.REQUEST_ID || "—"}
          </strong>
        </div>

        <div>
          <span>Started</span>
          <strong>
            {status?.STARTED_AT || "—"}
          </strong>
        </div>

        <div>
          <span>Capture</span>
          <strong>
            {status?.CAPTURE_ID || "—"}
          </strong>
        </div>

        <div>
          <span>Decode</span>
          <strong>
            {status?.AUTO_DECODE || "—"}
          </strong>
        </div>
      </div>

      <div className="remoteCaptureAudit">
        <div className="remoteCaptureAuditTitle">
          Recent Capture Audit
        </div>

        {audit.length === 0 ? (
          <p>
            No remote capture actions
            recorded yet.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Status</th>
                <th>Interface</th>
                <th>Duration</th>
              </tr>
            </thead>

            <tbody>
              {audit.slice(0, 10).map(
                (row) => (
                  <tr key={row.id}>
                    <td>
                      {row.created_at
                        ? new Date(
                            row.created_at
                          ).toLocaleString()
                        : "—"}
                    </td>

                    <td>
                      {row.actor || "—"}
                    </td>

                    <td>
                      {row.action}
                    </td>

                    <td>
                      {row.status}
                    </td>

                    <td>
                      {row.interface || "—"}
                    </td>

                    <td>
                      {row.duration_seconds
                        ? `${row.duration_seconds}s`
                        : "—"}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="remoteCaptureBoundary">
        Manual authorization required ·
        maximum 60 seconds ·
        maximum 50 MiB ·
        full-packet remote mode disabled ·
        capture default OFF
      </div>
    </section>
  );
}

export default RemoteCaptureControl;
